import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { logout } from './adminSlice'

const endpoint = process.env.VITE_API_ENDPOINT

const baseQuery = fetchBaseQuery({
  baseUrl: endpoint,
  credentials: 'include',
  fetchFn: async (requestInfo: RequestInfo, ...rest) =>
    fetch(requestInfo, ...rest),
  prepareHeaders: (headers: Headers) => {
    return headers
  },
})

/**
 * Detects requests where a 401 is expected to be handled by the caller.
 *
 * Called from the shared RTK Query base query before dispatching a logout, so
 * failed login attempts can show a form error instead of redirecting home.
 *
 * @param args - RTK Query request argument passed to `fetchBaseQuery`.
 * @returns True when the request targets the login endpoint.
 * @example isLoginRequest({ url: 'login' }) // true
 */
const isLoginRequest = (args: string | FetchArgs): boolean => {
  const url = typeof args === 'string' ? args : args.url
  return url === 'login' || url.endsWith('/login')
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)

  // Failed login stays on the login form; other 401 responses end the session.
  if (result.error && result.error.status === 401 && !isLoginRequest(args)) {
    // Dispatch logout action to update Redux state
    api.dispatch(logout())

    // Redirect to login page
    window.location.href = '/'
  }

  return result
}

export const API = createApi({
  reducerPath: 'RTK_Query',
  tagTypes: ['Posts', 'Tweets', 'PersonalAccessTokens'],
  keepUnusedDataFor: 30,
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    createPost: builder.mutation<Post, Req.CreatePost>({
      invalidatesTags: () => [{ type: 'Posts' }],
      query: (values) => ({
        body: values,
        method: 'POST',
        url: 'create',
      }),
    }),

    deletePost: builder.mutation<void, { id: Post['id'] }>({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'Posts' }],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `post/${id}/`,
      }),
    }),

    deleteStock: builder.mutation<void, { id: Stock['id'] }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `stock/${id}/`,
      }),
    }),

    fetchPost: builder.query<Post, Post['id']>({
      providesTags: (_result, _error, id) => [{ id, type: 'Posts' }],
      query: (id) => ({ method: 'GET', url: `post/${id}` }),
    }),

    fetchPostList: builder.query<Res.PostList, Req.PostList>({
      providesTags: (result) =>
        result && result.postList
          ? [
              ...result.postList.map(({ id }) => ({
                id,
                type: 'Posts' as const,
              })),
              { id: 'LIST', type: 'Posts' },
            ]
          : [{ id: 'LIST', type: 'Posts' }],
      query: ({ page, perPage }) => `post_list?page=${page}&perPage=${perPage}`,
    }),

    getStockList: builder.query<StockList, void>({
      query: () => ({
        method: 'GET',
        url: 'stocklist',
      }),
    }),

    getUserCount: builder.query<Res.GetUserCount, void>({
      query: () => ({
        method: 'GET',
        url: 'user_count',
      }),
    }),

    loginReqest: builder.mutation<Res.Login, Req.Login>({
      query: (loginInfo) => ({
        body: loginInfo,
        method: 'POST',
        url: 'login',
      }),
    }),

    logoutRequest: builder.mutation<Res.Logout, void>({
      query: () => ({
        method: 'POST',
        url: 'logout',
      }),
    }),

    signupReqest: builder.mutation<User, Req.Login>({
      query: (loginInfo) => ({
        body: loginInfo,
        method: 'POST',
        url: 'signup',
      }),
    }),

    validateToken: builder.query<{ valid: boolean; user?: User }, void>({
      query: () => ({
        method: 'GET',
        url: 'validate',
      }),
    }),

    updatePost: builder.mutation<Res.UpdatePost, Req.UpdatePost>({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'Posts' }],
      query: (values) => ({
        body: values,
        method: 'POST',
        url: 'update',
      }),
    }),
    fetchAllTweet: builder.query<any[], void>({
      query: () => ({ method: 'GET', url: 'tweet' }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Tweets' as const, id })),
              { type: 'Tweets', id: 'LIST' },
            ]
          : [{ type: 'Tweets', id: 'LIST' }],
    }),
    fetchTweetList: builder.query<Res.TweetList, Req.TweetList>({
      providesTags: (result) =>
        result && result.tweetList
          ? [
              ...result.tweetList.map(({ id }) => ({
                id,
                type: 'Tweets' as const,
              })),
              { id: 'LIST', type: 'Tweets' },
            ]
          : [{ id: 'LIST', type: 'Tweets' }],
      query: ({ page, perPage }) =>
        `tweet/tweet_list?page=${page}&perPage=${perPage}`,
    }),
    createTweet: builder.mutation({
      query: (text: string) => ({
        method: 'POST',
        url: 'tweet',
        body: { text },
      }),
      invalidatesTags: (result, _error) =>
        result ? [{ type: 'Tweets', id: 'LIST' }] : [],
    }),
    deleteTweet: builder.mutation<void, number>({
      query: (id: number) => ({
        method: 'DELETE',
        url: `tweet/${id}`,
      }),
      invalidatesTags: (result, _error, id) =>
        result
          ? [
              { type: 'Tweets', id },
              { type: 'Tweets', id: 'LIST' },
            ]
          : [],
    }),
    translateText: builder.mutation<
      {
        translatedText: string
        isTranslated: boolean
        originalText?: string
      },
      { text: string; from: string; to: string }
    >({
      query: (payload) => ({
        method: 'POST',
        url: 'translate',
        body: payload,
      }),
    }),
    postToBlueSky: builder.mutation<
      {
        success: boolean
        uri: string
        cid: string
        message: string
      },
      string
    >({
      query: (text: string) => ({
        method: 'POST',
        url: 'bluesky/post',
        body: { text },
      }),
    }),
    getHoverColorPreference: builder.query<
      { useLegacyHoverColors: boolean },
      void
    >({
      query: () => ({
        method: 'GET',
        url: 'hover-color-preference',
      }),
    }),
    updateHoverColorPreference: builder.mutation<
      { useLegacyHoverColors: boolean },
      boolean
    >({
      query: (useLegacyHoverColors: boolean) => ({
        method: 'PATCH',
        url: 'hover-color-preference',
        body: { useLegacyHoverColors },
      }),
    }),
    updateProfile: builder.mutation<User, { name?: string; password?: string }>(
      {
        query: (profileData) => ({
          method: 'PATCH',
          url: 'profile',
          body: profileData,
        }),
      },
    ),
    getPersonalAccessTokens: builder.query<Res.PersonalAccessTokenList, void>({
      providesTags: ['PersonalAccessTokens'],
      query: () => ({
        method: 'GET',
        url: 'personal_access_token/list',
      }),
    }),
    mintPersonalAccessToken: builder.mutation<
      Res.MintPersonalAccessToken,
      { name: string }
    >({
      invalidatesTags: ['PersonalAccessTokens'],
      query: (body) => ({
        method: 'POST',
        url: 'personal_access_token',
        body,
      }),
    }),
    revokePersonalAccessToken: builder.mutation<
      Res.RevokePersonalAccessToken,
      { id: number }
    >({
      invalidatesTags: ['PersonalAccessTokens'],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `personal_access_token/${id}`,
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserCountQuery,
  useSignupReqestMutation,
  useFetchAllTweetQuery,
  useFetchTweetListQuery,
  useCreateTweetMutation,
  useDeleteTweetMutation,
  useTranslateTextMutation,
  usePostToBlueSkyMutation,
  useGetHoverColorPreferenceQuery,
  useUpdateHoverColorPreferenceMutation,
  useUpdateProfileMutation,
  useGetPersonalAccessTokensQuery,
  useMintPersonalAccessTokenMutation,
  useRevokePersonalAccessTokenMutation,
} = API
